// THE RUN-EVENT SIZE BOUND, in one home (#6765, epic #6552). The worker holds every box
// payload to it at persist (`apps/worker/src/fleet/box-run-event-insert.ts`,
// `boundRunEventPayload`), and the runner's scout holds its `scout_doable` hand-off STRICTLY
// under it before emitting (`apps/runner/src/modes/scout/brain/handoff.ts`), because a cut
// hand-off brief is a silently worse plan. Both sides measure with {@link jsonBytes}, so
// they cannot disagree about what a byte is.
//
// Browser-imported (`@developerz/domain` reaches the dashboard), so the measure is
// `TextEncoder`, never Node's `Buffer`.

/**
 * The largest box payload persisted as sent, in UTF-8 bytes of its JSON (#6422).
 *
 * The failure it prevents: a provider's multi-MB HTML error page carried verbatim in an
 * `error` payload is stored once, then `JSON.stringify`'d into every viewer's SSE frame
 * and every audit projection of the row. The stream admits 8 MB a message
 * (`EVENTS_STREAM_MAX_MSG_BYTES`), so the wire is no bound.
 *
 * 256 KiB, measured: prod's largest payload in the 7 days to 2026-10-17 was 178,863
 * bytes (a `status` row; 13 of 662,063 rows were over 64 KiB, none over 256 KiB). So
 * the bound touches no payload the fleet sends today and still caps the next one at
 * 1/32 of what the stream would carry.
 */
export const RUN_EVENT_PAYLOAD_MAX_BYTES = 256 * 1024;

/** What an over-bound payload's long strings are cut to (chars), head and tail kept. */
export const RUN_EVENT_STRING_CUT_CHARS = 16 * 1024;

/**
 * What the persisted row adds around the producer's `scout_doable` object: the worker's
 * trusted stamp (`fleet_origin`) and whatever redaction rewrites. Held back from the
 * producer's bound so the row the worker stores is still under
 * {@link RUN_EVENT_PAYLOAD_MAX_BYTES} and is never cut.
 */
export const SCOUT_DOABLE_ROW_HEADROOM_BYTES = 16 * 1024;

/**
 * The `scout_doable` producer's STRICT bound: the box requires
 * `jsonBytes(payload) < SCOUT_DOABLE_MAX_BYTES` before it emits. The Planner refuses a
 * hand-off the persist had to cut (`payload_truncation`), so a payload over this would be
 * a hand-off nobody can plan from.
 */
export const SCOUT_DOABLE_MAX_BYTES = RUN_EVENT_PAYLOAD_MAX_BYTES - SCOUT_DOABLE_ROW_HEADROOM_BYTES;

const encoder = new TextEncoder();

/** The UTF-8 byte length of `value`'s JSON — what the persisted row and every bound measure. */
export function jsonBytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).byteLength;
}
